import axios from 'axios';
import {
  LevelCompletedEvent,
  LevelPlayEvent,
  LevelPlayingEvent,
  RepertoirePlayingEvent,
  TierCompletedEvent,
  TierStartedEvent,
  LevelStartedEvent,
  EventTypes,
  EventType,
  ExercisePlaythroughCompletedEvent,
} from 'Types/EventTypes';
import logger from 'Utils/Logger';
import { reportError } from 'Utils/reportError';

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;
const eventMaxBatchSize = process.env.REACT_APP_EVENT_MAX_BATCH_SIZE;
if (!eventMaxBatchSize) {
  throw Error('REACT_APP_EVENT_MAX_BATCH_SIZE is not set');
}

const eventMaxBatchSizeInt = parseInt(eventMaxBatchSize);

type BaseEventType = {
  datetime: string;
  analytics_session_id: string;
  // MUS-418: per-event idempotency key (uuid v4), stamped at enqueue in the saga.
  client_event_id?: string;
};

type PlaySessionEventType = BaseEventType & { play_session_id?: string };

type PlaySessionEvents = PlaySessionEventType &
  (
    | LevelCompletedEvent
    | LevelPlayEvent
    | LevelPlayingEvent
    | RepertoirePlayingEvent
    | TierCompletedEvent
    | TierStartedEvent
    | LevelStartedEvent
    | ExercisePlaythroughCompletedEvent
  );

type GenericEventType = (BaseEventType & EventTypes) | PlaySessionEvents;

// MUS-414: the /events batch handler no longer 422s a whole batch on a bad event.
// It returns a per-event partial-success summary. unknown / duplicate / invalid ride
// a 200; only a genuine server persist fault (failed[]) still yields a 422 (carrying
// this same body). MUS-378 reads this shape to tell a real client-error from a benign
// skip. Fields are optional because we parse a response we did not author.
type EventsBatchSummary = {
  persisted?: number;
  skipped?: {
    unknown?: number;
    duplicate?: number;
    invalid?: Array<{ event_type?: string; reason?: unknown }>;
  };
  failed?: Array<{ event_type?: string; reason?: unknown }>;
};
// Real user pauses/repeats are spaced much further apart; double-fires from
// effect closures, paired listeners, or upstream Phaser duplicates almost
// always land within a few hundred ms of each other.
const DEDUPE_WINDOW_MS = 1000;

// Fields stripped from the dedup signature so events with otherwise-identical
// payloads are recognized as duplicates. `datetime` differs by definition;
// the others are session-scoped and identical across consecutive emits anyway.
// `client_event_id` (MUS-418) is a fresh uuid per enqueue, so it MUST be ignored
// here - otherwise every event would have a unique signature and this near-duplicate
// window dedup would never collapse anything. (The backend uses client_event_id for
// its own retry dedup; the two mechanisms are independent.)
const DEDUPE_IGNORED_FIELDS = new Set([
  'datetime',
  'analytics_session_id',
  'platform',
  'client_event_id',
]);

function buildDedupeSignature(event: GenericEventType): string {
  const keyFields: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(event)) {
    if (!DEDUPE_IGNORED_FIELDS.has(key)) {
      keyFields[key] = value;
    }
  }
  return JSON.stringify(keyFields);
}

class EventQueue {
  queue: GenericEventType[] = [];

  pushEvent(event: GenericEventType) {
    this.queue.push(event);
  }

  // Drop events whose (event_type + payload-minus-temporal-fields) signature
  // already appeared within DEDUPE_WINDOW_MS. Catches the client-side double-
  // emit pattern (MUS-232) without losing intentional repeated actions.
  filterRecentDuplicates(queue: GenericEventType[]): GenericEventType[] {
    const lastSeenAt = new Map<string, number>();
    const result: GenericEventType[] = [];
    for (const event of queue) {
      const signature = buildDedupeSignature(event);
      const eventTime = new Date(event.datetime).getTime();
      const previousTime = lastSeenAt.get(signature);
      if (previousTime !== undefined && eventTime - previousTime < DEDUPE_WINDOW_MS) {
        logger.debug('dropping duplicate event within dedupe window', {
          event_type: event.event_type,
          gap_ms: eventTime - previousTime,
        });
        continue;
      }
      lastSeenAt.set(signature, eventTime);
      result.push(event);
    }
    return result;
  }

  batchEvents(queue: GenericEventType[]): GenericEventType[][] {
    const batches: GenericEventType[][] = [[]];
    let indx = 0;
    while (queue.length > 0) {
      // logger.info("queue is larger than zero")
      // logger.info("queue.length", queue.length)
      if (batches[indx].length <= eventMaxBatchSizeInt) {
        batches[indx].push(queue.shift());
      } else {
        batches.push([queue.shift()]);
        indx += 1;
      }
    }
    return batches;
  }

  filterDuplicateEvents(queue: GenericEventType[]) {
    let latestLevelPlayingEvent: (LevelPlayingEvent & PlaySessionEventType) | undefined,
      latestTierCompletedEvent: (TierCompletedEvent & PlaySessionEventType) | undefined,
      latestLevelCompletedEvent: (LevelCompletedEvent & PlaySessionEventType) | undefined;

    const filteredQeue = queue.reduce(
      (accumulatedEvents: GenericEventType[], currentEvent: GenericEventType) => {
        switch (currentEvent.event_type) {
          case EventType.LevelCompletedEvent:
            if (
              !latestLevelCompletedEvent ||
              new Date(currentEvent.datetime) > new Date(latestLevelCompletedEvent.datetime)
            ) {
              latestLevelCompletedEvent = currentEvent;
            }
            break;
          case EventType.LevelPlayingEvent:
            if (!latestLevelPlayingEvent || currentEvent.play_time > currentEvent.play_time) {
              latestLevelPlayingEvent = currentEvent;
            }
            break;
          case EventType.TierCompletedEvent:
            if (
              !latestTierCompletedEvent ||
              new Date(currentEvent.datetime) > new Date(latestTierCompletedEvent.datetime)
            ) {
              latestTierCompletedEvent = currentEvent;
            }
            break;
          default:
            accumulatedEvents.push(currentEvent);
        }
        if (
          !latestLevelCompletedEvent &&
          currentEvent.event_type === EventType.LevelCompletedEvent
        ) {
          latestLevelCompletedEvent = currentEvent;
          accumulatedEvents.push(currentEvent);
        }
        return accumulatedEvents;
      },
      [],
    );

    for (const evt of [
      latestLevelCompletedEvent,
      latestLevelPlayingEvent,
      latestTierCompletedEvent,
    ]) {
      if (evt) {
        filteredQeue.push(evt);
      }
    }
    return filteredQeue;
  }

  // MUS-378: surface a partial-success summary's real client errors to RUM.
  // "Real" = an event the backend won't persist: skipped.invalid (a known type that
  // failed validation, e.g. MUS-377's rh_accuracy NOT-NULL) or failed (a server persist
  // fault). skipped.duplicate (retry dedup) and skipped.unknown (a type-registry gap
  // already logged server-side by MUS-414) are benign and intentionally NOT reported,
  // to keep RUM free of alarm noise. Emits at most one RUM error, and returns true when
  // it did, so the caller can avoid a second, redundant emit for the same failure.
  private reportRejectedEntries(
    summary: EventsBatchSummary | undefined,
    httpStatus: number | undefined,
  ): boolean {
    const invalid = summary?.skipped?.invalid ?? [];
    const failed = summary?.failed ?? [];
    if (invalid.length === 0 && failed.length === 0) {
      return false;
    }
    const detail = {
      httpStatus,
      invalidCount: invalid.length,
      failedCount: failed.length,
      invalidTypes: [...new Set(invalid.map((e) => e?.event_type ?? 'unknown'))],
      failedTypes: [...new Set(failed.map((e) => e?.event_type ?? 'unknown'))],
    };
    reportError(new Error(`events POST rejected entries ${JSON.stringify(detail)}`), 'events.post');
    return true;
  }

  // MUS-378: the events POST used to be swallowed - the catch only logger.error'd and
  // the success body was discarded. Now one path handles both:
  //  - a 2xx can still carry per-event rejections in its body (MUS-414) -> report them;
  //  - a non-2xx (network error / 4xx / the 422 that carries failed[]) was previously
  //    invisible in production -> report it, then keep the existing log breadcrumb.
  // Never throws (matches the prior swallow contract, so one bad batch does not abort the
  // rest of the queue flush).
  private async postBatch(batch: GenericEventType[]): Promise<void> {
    try {
      const response = await axios.post<EventsBatchSummary>(baseUrl + 'events', batch);
      this.reportRejectedEntries(response?.data, response?.status);
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      const data = axios.isAxiosError(err)
        ? (err.response?.data as EventsBatchSummary | undefined)
        : undefined;
      // The 422 partial-success case carries failed[]/invalid[] in the error response;
      // that structured report is the informative one when present.
      const reportedEntries = this.reportRejectedEntries(data, status);
      // Otherwise (network error, or a 4xx/5xx with no summary body) surface the transport
      // failure itself so it is visible in RUM rather than only in the console.
      if (!reportedEntries) {
        reportError(err, 'events.post');
      }
      logger.error('events batch error');
      logger.error(err);
    }
  }

  async clearQueue() {
    const dedupedRecent = this.filterRecentDuplicates(this.queue);
    const deduped = this.filterDuplicateEvents(dedupedRecent);
    const batches = this.batchEvents(deduped);
    if (batches.length === 0) {
      logger.debug('no events to send');
      return;
    }
    logger.debug('sending batch of size ' + batches[0].length);
    // setting queue to empty as quickly as possible to prevent multiple simultaneous calls from
    // sending dupe events.
    this.queue = [];
    await this.postBatch(batches[0]);
    for (const batch of batches.slice(1, batches.length)) {
      await new Promise((prom) => setTimeout(prom, 200));
      await this.postBatch(batch);
    }
    logger.debug('queue cleared');
  }

  async dispatchEvents(callback?: () => void) {
    if (process.env.REACT_APP_SEND_EVENTS == 'true') {
      try {
        if (this.queue.length > 0) {
          await this.clearQueue();
        }
        if (callback) {
          callback();
        }
      } catch (error) {
        console.error(error);
      }
    } else {
      logger.debug('REACT_APP_SEND_EVENTS not set - not sending events.');
    }
  }
}

// singleton is important here
export const eventQueue = new EventQueue();
